import React, { ReactNode, useEffect, useRef } from 'react';
import {
  IQRCodeContentCommonProps,
  QRCodeContentLocalStorageKeys,
  QRCodeContentNavigationActions,
  QRCodeScreenTypes,
} from '../../QRCodeCommonComponentEnums';
import QRCodeContentViewWrapper from '../QRCodeViewWrapper';
import PIEModal from '@src/shared/components/PIEModal';
import PIEText from '@src/shared/components/PIEText';
import PIEBadge from '@src/shared/components/PIEBadge';
import styles from './QRCodeContent.module.scss';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import QRCode from 'react-qr-code';
import {
  IconCheckCircle,
  IconClose,
  IconHelpCircle,
  IconRefresh,
  IconSend,
} from '@justeattakeaway/pie-icons-react';
import PIEButton from '@src/shared/components/PIEButton';
import { useTranslation } from 'react-i18next';
import DeliveryDashboardBottomSheet from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { isMobile } from 'react-device-detect';
import ConnectionCodeContainer from '../ConnectionCodeContainer';
import PIELoading from '@src/shared/components/PIELoading';
import PIEToast from '@src/shared/components/PIEToast';

const BOTTOM_SHEET_HEADER_HEIGHT = 72;
const MOBILE_THRESHOLD_HEIGHT = 740;
const QR_DIVIDER_SMALL_SCREEN = 3;
const QR_DIVIDER_LARGE_SCREEN = 2;
const QR_SIZE_OFFSET = 110;
const QR_CODE_DEFAULT_SIZE = 256;
const QR_CODE_VIEW_BOX_SIZE = 256;

export enum QRCodeContentDataRetrieveErrorCodes {
  RETRY = 429,
  SERVER_ERROR = 500,
}

interface componentProps extends IQRCodeContentCommonProps {
  predefinedQRSize?: number;
  qrCode: string;
  connectionCode: string;
  connectionCodeVersion: number;
  lastSeenVersion: number;
  onVersionAcknowledged: () => void;
  isComponentBusy: boolean;
  onCodeRegenerate: () => void;
  dataRetrieveErrorCode?: any;
}

const QRCodeContent = ({
  onNavigate,
  onCodeRegenerate,
  bottomSheetView,
  popupView,
  predefinedQRSize,
  additionalParameters,
  qrCode,
  connectionCode,
  connectionCodeVersion,
  lastSeenVersion,
  onVersionAcknowledged,
  isComponentBusy,
  dataRetrieveErrorCode,
}: componentProps) => {
  const { t } = useTranslation();
  const { isResolutionMobile, isTablet } = useDeviceDetect();
  const [showQRDataRetrieveResultToast, setShowQRDataRetrieveResultToast] =
    React.useState<boolean>(false);

  const [showQRDataRetrieveErrorToast, setShowQRDataRetrieveErrorToast] =
    React.useState<boolean>(false);
  const hasAcknowledgedVersion = useRef<boolean>(false);

  useEffect(() => {
    // Show toast if version changed since last time this screen was viewed
    if (connectionCodeVersion > lastSeenVersion && !hasAcknowledgedVersion.current) {
      setShowQRDataRetrieveResultToast(false);
      setShowQRDataRetrieveErrorToast(false);
      requestAnimationFrame(() => {
        setShowQRDataRetrieveResultToast(true);
      });
      hasAcknowledgedVersion.current = true;
      onVersionAcknowledged();
    }
  }, [connectionCodeVersion, lastSeenVersion, onVersionAcknowledged]);

  // Reset acknowledged flag when version changes while on this screen
  useEffect(() => {
    hasAcknowledgedVersion.current = false;
  }, [connectionCodeVersion]);

  useEffect(() => {
    if (dataRetrieveErrorCode) {
      setShowQRDataRetrieveResultToast(false);
      setShowQRDataRetrieveErrorToast(false);
      requestAnimationFrame(() => {
        setShowQRDataRetrieveErrorToast(true);
      });
    } else {
      setShowQRDataRetrieveErrorToast(false);
    }
  }, [dataRetrieveErrorCode]);

  const calculateQRSize = () => {
    if (predefinedQRSize) {
      return predefinedQRSize;
    }
    if (isResolutionMobile || (isTablet && bottomSheetView)) {
      const bottomSheetHeight = screen.height - BOTTOM_SHEET_HEADER_HEIGHT;
      const divider =
        screen.height <= MOBILE_THRESHOLD_HEIGHT
          ? QR_DIVIDER_SMALL_SCREEN
          : QR_DIVIDER_LARGE_SCREEN;
      return bottomSheetHeight / divider - QR_SIZE_OFFSET;
    } else if (!isResolutionMobile) {
      return QR_CODE_DEFAULT_SIZE;
    }
  };

  const onCloseHander = () => {
    if (!isComponentBusy) {
      onNavigate && onNavigate(QRCodeContentNavigationActions.CLOSE);
    }
  };

  const handleShareFunctionality = () => {
    if (navigator.share && (isMobile || isTablet)) {
      try {
        navigator.share({
          title: t('ocf_screens.QR_CODE_SHARE_TITLE'),
          text: connectionCode,
        });
      } catch (error) {
        onNavigate?.(QRCodeContentNavigationActions.JUMP_SCREEN, QRCodeScreenTypes.SHARE_QR_CODE);
      }
    } else {
      onNavigate?.(QRCodeContentNavigationActions.JUMP_SCREEN, QRCodeScreenTypes.SHARE_QR_CODE);
    }
  };

  const handleRegenerateFunctionality = () => {
    const { QR_CODE_REGENERATE_DONT_SHOW_AGAIN } = QRCodeContentLocalStorageKeys;
    const dontShowAgain = localStorage.getItem(QR_CODE_REGENERATE_DONT_SHOW_AGAIN);
    if (!dontShowAgain) {
      onNavigate?.(
        QRCodeContentNavigationActions.JUMP_SCREEN,
        QRCodeScreenTypes.RE_GENERATE_QR_CODE
      );
    } else {
      executeRegeneration();
    }
  };

  const executeRegeneration = () => {
    onCodeRegenerate();
  };

  useEffect(() => {
    const { regenerate } = additionalParameters || {};
    if (regenerate) {
      executeRegeneration();
    }
  }, [additionalParameters]);
  const renderMobileView = (content: ReactNode, overrideBlockingValue?: boolean | undefined) => {
    return (
      <DeliveryDashboardBottomSheet
        blocking={overrideBlockingValue !== undefined ? overrideBlockingValue : true}
        header={
          <>
            <PIEText
              align="left"
              size={'var(--xds-font-size-20)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_MODAL_TITLE')}
            />
          </>
        }
        showCloseButton={true}
        onBottomSheetHeight={() => {}}
        constantHeight={screen.height - BOTTOM_SHEET_HEADER_HEIGHT}
        onClose={onCloseHander}
      >
        <PIEComponentUtil padding={'var(--xds-spacing-d)'}>{content}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    );
  };

  const renderDesktopView = (content: ReactNode) => {
    return (
      <PIEModal isOpen={true} size="small" modOverlay>
        <PIEModal.Header
          onClose={onCloseHander}
          heading={
            <PIEText
              size={'var(--xds-font-size-24)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_MODAL_TITLE')}
            />
          }
        />
        <PIEModal.Content>{content}</PIEModal.Content>
      </PIEModal>
    );
  };

  const getToastMessage = () => {
    if (showQRDataRetrieveResultToast) {
      return t('ocf_screens.QR_CODE_GENERATED_SUCCESS_MESSAGE');
    }
    if (
      showQRDataRetrieveErrorToast &&
      dataRetrieveErrorCode === QRCodeContentDataRetrieveErrorCodes.RETRY
    ) {
      return t('ocf_screens.QR_CODE_GENERATED_TIME_LIMIT_ERROR_MESSAGE');
    }

    if (
      showQRDataRetrieveErrorToast &&
      dataRetrieveErrorCode === QRCodeContentDataRetrieveErrorCodes.SERVER_ERROR
    ) {
      return t('ocf_screens.QR_CODE_GENERATED_GENERIC_ERROR_MESSAGE');
    }
  };

  const renderContent = () => {
    return (
      <>
        <PIEComponentUtil marginBottom="var(--xds-spacing-c)">
          <PIEText
            size={'var(--xds-font-size-14)'}
            fontWeight="var(--xds-font-weight-regular)"
            text={
              <div className={styles.articleContainer}>
                <span className={styles.badgeContainer}>
                  <PIEBadge align="t-l">1</PIEBadge>
                </span>
                {t('ocf_screens.QR_CODE_ARTICLE_1_TEXT')}
              </div>
            }
          />
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-c)">
          <PIEText
            size={'var(--xds-font-size-14)'}
            fontWeight="var(--xds-font-weight-regular)"
            text={
              <div className={styles.articleContainer}>
                <span className={styles.badgeContainer}>
                  <PIEBadge align="t-l">2</PIEBadge>
                </span>
                {t('ocf_screens.QR_CODE_ARTICLE_2_TEXT')}
              </div>
            }
          />
        </PIEComponentUtil>
        <div className={styles.qrCodeContainer} id="qr-code-container">
          {isComponentBusy && (
            <div className={styles.qrCodeContainerLoadingScreen}>
              <PIELoading isActive size="l" qaSelector={'qr-code-loading-indicator'} />
            </div>
          )}
          <QRCode
            size={calculateQRSize()}
            style={{
              height: `${calculateQRSize()}px`,
              maxHeight: 'none',
              maxWidth: 'none',
              width: '100%',
            }}
            value={qrCode}
            viewBox={`0 0 ${QR_CODE_VIEW_BOX_SIZE} ${QR_CODE_VIEW_BOX_SIZE}`}
          />
        </div>
        <PIEComponentUtil marginBottom="var(--xds-spacing-b)">
          <PIEText
            size={'var(--xds-font-size-14)'}
            fontWeight="var(--xds-font-weight-regular)"
            text={
              <span
                dangerouslySetInnerHTML={{
                  __html: t('ocf_screens.QR_CODE_CONNECTION_CODE_INSTRUCTION'),
                }}
              />
            }
          />
        </PIEComponentUtil>
        <div
          style={{ opacity: isComponentBusy ? '0.1' : '' }}
          id="connection-code-container-wrapper"
        >
          <ConnectionCodeContainer code={connectionCode} />
        </div>
        <div className={styles.toastOuterContainer}>
          <div className={styles.toastInnerContainer}>
            {showQRDataRetrieveResultToast || showQRDataRetrieveErrorToast ? (
              <PIEToast
                isOpen={true}
                modFluid
                modCompact
                mode={showQRDataRetrieveResultToast ? 'regular' : 'error'}
                autoCloseTimeout={4000}
                primaryActionButtonProps={{
                  children: <IconClose size="s" />,
                  onClick: () => {
                    setShowQRDataRetrieveResultToast(false);
                    setShowQRDataRetrieveErrorToast(false);
                  },
                }}
              >
                {showQRDataRetrieveResultToast && (
                  <IconCheckCircle size="s" color="var(--xds-color-support-positive-inverse)" />
                )}
                {getToastMessage()}
              </PIEToast>
            ) : (
              <></>
            )}
          </div>
        </div>
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)">
          <PIEText
            size={'var(--xds-font-size-16)'}
            fontWeight="var(--xds-font-weight-bold)"
            align="center"
            text={
              <div
                className={styles.helpContainer}
                onClick={() => {
                  onNavigate?.(
                    QRCodeContentNavigationActions.JUMP_SCREEN,
                    QRCodeScreenTypes.QR_CODE_FAQ
                  );
                }}
              >
                <IconHelpCircle size="s" />
                <u>{t('ocf_screens.QR_CODE_HELP_LINK')}</u>
              </div>
            }
          />
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEButton
            id={'qr-code-share-button'}
            disabled={isComponentBusy}
            autoWidth
            icon={<IconSend size="s" />}
            size="medium"
            mode="primary"
            type="button"
            text={t('ocf_screens.QR_CODE_SHARE_BUTTON')}
            onClick={() => handleShareFunctionality()}
          ></PIEButton>
        </PIEComponentUtil>
        <PIEComponentUtil>
          <PIEButton
            disabled={isComponentBusy}
            onClick={() => handleRegenerateFunctionality()}
            autoWidth
            icon={<IconRefresh size="s" color="var(--xds-color-interactive-brand)" />}
            size="medium"
            mode="secondary"
            type="button"
            text={t('ocf_screens.QR_CODE_GENERATE_NEW_CODE_BUTTON')}
          ></PIEButton>
        </PIEComponentUtil>
      </>
    );
  };

  return (
    <QRCodeContentViewWrapper
      content={renderContent()}
      bottomSheetView={bottomSheetView}
      popupView={popupView}
      bottomSheetWrapper={renderMobileView}
      popupWrapper={renderDesktopView}
    />
  );
};

export default QRCodeContent;
